import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'react-redux';
import { message } from "antd";
import { queryClient } from './lib/queryClient';
import store from './pages/interactive/store';
import MqttProvider from './context/MqttContext';
import { FileManagementProvider } from './context/FileManagementContext';
import { DemandProvider } from './context/DemandContext';
import Home from "./pages/Home";
import Recruit from "./pages/Recruit";
import Combo from "./pages/Combo";
import Member from "./pages/Member";
import LiveStream from "./pages/LiveStream";
import Interactive from "./pages/interactive";
import Layout from "./components/Layout";
import NotFound from "./components/NotFound";
import { useAppData } from "./hooks/useAppData";

// 創建一個 Providers 組件來管理所有的 Context Providers
const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <MqttProvider>
          <FileManagementProvider>
            <DemandProvider>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                {children}
              </BrowserRouter>
            </DemandProvider>
          </FileManagementProvider>
        </MqttProvider>
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </Provider>
  );
};

// 創建一個 AppContent 組件來處理需要 QueryClient 的邏輯
const AppContent: React.FC = () => {
  const { companyInfo, mainData, footerData, isLoading, error } = useAppData();

  // 設置文檔標題
  useEffect(() => {
    if (companyInfo.name) {
      document.title = companyInfo.name;
    }
  }, [companyInfo.name]);

  // 錯誤處理
  useEffect(() => {
    if (error) {
      console.error('App 數據獲取失敗:', error);
      message.error('無法獲取數據');
    }
  }, [error]);

  // 創建帶 Layout 的路由組件
  const createLayoutRoute = (
    component: React.ReactElement,
    mode: 'default' | 'no-banner' | 'fullscreen' = 'default'
  ) => (
    <Layout
      companyInfo={companyInfo}
      mainData={mainData}
      footerData={footerData}
      loading={isLoading}
      mode={mode}
    >
      {component}
    </Layout>
  );

  return (
    <Routes>
      {/* 默認 Layout 的路由 */}
      <Route path="/" element={createLayoutRoute(<Home mainData={mainData} />)} />
      <Route path="/combo" element={createLayoutRoute(<Combo />)} />
      <Route path="/combo/:demandId" element={createLayoutRoute(<Combo />)} />
      <Route path="/join" element={createLayoutRoute(<Recruit />)} />
      <Route path="/member" element={createLayoutRoute(<Member />, 'no-banner')} />
      <Route path="*" element={createLayoutRoute(<NotFound />)} />

      {/* 特殊 Layout 的路由 */}
      <Route path="/livestream" element={createLayoutRoute(<LiveStream />, 'no-banner')} />
      <Route path="/interactive" element={createLayoutRoute(<Interactive />, 'fullscreen')} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Providers>
      <AppContent />
    </Providers>
  );
};

export default App;
